<template>
  <div v-if="loading">
    <spinner />
  </div>
  <div v-else class="container">
    <sport-subscription-plan-list
      v-if="activeStep === 1"
      :initial-values="subscriptionPlans"
      :organization="organization"
      :organization-slug="organizationSlug"
      @createNewPlan="activeStep = 2"
      @editPlan="handleEdit($event)"
      @delete="handleDelete"
      @refetch="fetchPrepaidCoupons"
    />
    <create-or-edit-sport-subscription-plan
      v-if="activeStep === 2"
      :event-templates="organizationEventTemplates"
      :interests="organizationInterests"
      :initial-value="initialValue"
      :all-events="allEvents"
      :organization="organization"
      @cancel="handleReset"
      @create="handleCreate"
      @update="handleUpdate"
    />
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="organizationText(snackbar.message)"
      :color="snackbar.color"
    />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import {
  DELETE_ORGANIZATION_PREPAID_COUPON,
  GET_SPORT_PREPAID_COUPONS_WITH_EVENTS_AND_ORGANIZATION,
  CREATE_SPORT_ORGANIZATION_PREPAID_COUPON,
  UPDATE_SPORT_ORGANIZATION_PREPAID_COUPON,
} from '../../../graphql/prepaidCoupon.graphql'
import Spinner from './../../shared/spinner.vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import { omit } from 'lodash'
import CreateOrEditSportSubscriptionPlan from '@/components/organizations/subscriptions/CreateOrEditSportSubscriptionPlan.vue'
import SportSubscriptionPlanList from '@/components/organizations/subscriptions/SportSubscriptionPlanList.vue'

export default {
  mixins: [OrganizationLocalization],
  name: 'sport-subscriptions-plan-container',
  components: {
    SportSubscriptionPlanList,
    CreateOrEditSportSubscriptionPlan,
    SimpleSnackbar,
    Spinner,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      activeStep: 1,
      subscriptionPlans: [],
      loading: true,
      organizationEventTemplates: [],
      organizationInterests: [],
      initialValue: {},
      snackbar: {
        open: false,
        message: '',
        color: 'primary',
      },
      allEvents: [],
      organization: null,
    }
  },
  async mounted() {
    await this.fetchPrepaidCoupons()
  },
  methods: {
    async handleCreate(info) {
      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_SPORT_ORGANIZATION_PREPAID_COUPON,
        variables: {
          organizationSlug: this.organizationSlug,
          prepaidCouponInput: info,
        },
      })
      await this.showSnackbarMsgAndRefetchData(errors, 'Plan created')
    },
    modifyCouponItemData(couponItems) {
      return couponItems.map(item => ({
        ...omit(item, ['externalId']),
      }))
    },
    async handleUpdate(info) {
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_SPORT_ORGANIZATION_PREPAID_COUPON,
        variables: {
          organizationSlug: this.organizationSlug,
          prepaidCouponInput: omit(info, ['prepaidCouponUser', '__typename']),
        },
      })
      await this.showSnackbarMsgAndRefetchData(errors, 'Plan updated')
    },
    async handleDelete(selected) {
      if (
        selected.some(prepaidCouponId => !this.checkIfPrepaidCouponCanBeDeleted(prepaidCouponId))
      ) {
        this.snackbar = {
          open: true,
          message: 'A prepaid coupon that has already been purchased cannot be deleted.',
          color: 'error',
        }

        return
      }
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_ORGANIZATION_PREPAID_COUPON,
        variables: {
          organizationSlug: this.organizationSlug,
          prepaidCouponIds: selected.map(s => parseInt(s)),
        },
      })
      this.activeStep = 0
      await this.showSnackbarMsgAndRefetchData(errors, 'Plan deleted')
    },
    async handleRefetchData() {
      await this.fetchPrepaidCoupons()
      this.handleReset()
    },
    async showSnackbarMsgAndRefetchData(errors, msg) {
      if (errors) {
        this.snackbar = {
          open: true,
          message: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          message: this.organizationMessage('organizationAll.value0_successfully_4bf9e6b5', '{{value0}} successfully!', { value0: msg }),
        }
        await this.handleRefetchData()
      }
    },
    handleEdit(selectedPlanId) {
      this.initialValue = this.subscriptionPlans.find(plan => plan.id === selectedPlanId)
      this.activeStep = 2
    },
    handleReset() {
      this.activeStep = 1
      this.initialValue = {}
    },
    async fetchPrepaidCoupons() {
      const { data, errors } = await this.$apollo.query({
        query: GET_SPORT_PREPAID_COUPONS_WITH_EVENTS_AND_ORGANIZATION,
        variables: {
          organizationSlug: this.organizationSlug,
        },
      })
      if (errors) {
        await this.showSnackbarMsgAndRefetchData(errors)
      } else {
        this.subscriptionPlans = data.getSportOrganizationPrepaidCoupons
        this.allEvents = data.getTicketingEventsByOrganizationSlug
        this.organization = data.getOrganizationBySlug
        this.loading = false
      }
    },
    checkIfPrepaidCouponCanBeDeleted(prepaidCouponId) {
      const prepaidCoupon = this.subscriptionPlans.find(plan => plan.id === prepaidCouponId)

      return !!prepaidCoupon && !prepaidCoupon.prepaidCouponUser.length
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 43.75rem;
}
</style>
